import { NgModule, NO_ERRORS_SCHEMA, ErrorHandler } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ToggleFullScreenDirective } from './directive/fullscreen/toggle-fullscreen.directive';
import { AccordionAnchorDirective } from './directive/accordion/accordionanchor.directive';
import { AccordionLinkDirective } from './directive/accordion/accordionlink.directive';
import { AccordionDirective } from './directive/accordion/accordion.directive';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { GlobalErrorHandlerService } from './service/error-service/global-error-handler.service';
import {
  PERFECT_SCROLLBAR_CONFIG,
  PerfectScrollbarConfigInterface,
  PerfectScrollbarModule,
} from 'ngx-perfect-scrollbar';
import { CardComponent } from './component/card/card.component';
import { CardToggleDirective } from './component/card/card-toggle.directive';
import { ModalBasicComponent } from './component/modal-basic/modal-basic.component';
import { ModalAnimationComponent } from './modal-animation/modal-animation.component';
import { SpinnerComponent } from './component/spinner/spinner.component';
import { ClickOutsideModule } from 'ng-click-outside';
import { DataFilterPipe } from './pipe/data-filter.pipe';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { CustomMaxDirective } from './directive/validators/custom-max-validator.directive';
import { CustomMinDirective } from './directive/validators/custom-min-validator.directive';
import { CustomMinDateDirective } from './directive/validators/custom-min-date-validator.directive';
import { CustomMaxDateDirective } from './directive/validators/custom-max-date-validator.directive';
import { AppHttpInterceptor } from '../http.interceptor';
import { Ng2SmartTableModule } from 'ng2-smart-table';
import { OwlDateTimeModule, OwlNativeDateTimeModule } from 'ng-pick-datetime-ex';
import { DatePickerComponent, DatePickerRendererComponent } from './component/date-picker/date-picker.component';
import { DatatableComponent } from './component/datatable/datatable.component';
import { TreeModule } from '@circlon/angular-tree-component';
import { TreeCheckboxComponent } from './component/tree-checkbox/tree-checkbox.component';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { CustomMaxLengthValidatorDirective } from './directive/validators/custom-max-length-validator.directive';
import { DateRangePickerComponent } from './component/date-range-picker/date-range-picker.component';
import { SelectModule } from 'ng-select';
import { UiSwitchModule } from 'ngx-ui-switch';
import { NgxEchartsModule } from 'ngx-echarts';
import { ArchwizardModule } from 'angular-archwizard';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { AngularMultiSelectModule } from 'angular2-multiselect-dropdown';
import { MainNavBarComponent } from './component/main-nav-bar/main-nav-bar.component';
import { RouterModule } from '@angular/router';
import { FilterCardModule } from './component/filter/filter-card/filter-card.module';
import { SearchBoxComponent } from './component/filter/search-box/search-box.component';
import { SideConfigBarComponent } from './component/side-config-bar/side-config-bar.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatTabsModule } from '@angular/material/tabs';
import { DisableControlDirective } from './directive/disable-control-directive';
import { PaginationCustomizerDirective } from './directive/pagination-customizer/pagination-customizer.directive';
import { NumberInputComponent } from './component/smart-table/number-input/number-input.component';
import { SearchableDropdownComponent } from './component/smart-table/searchable-dropdown/searchable-dropdown.component';
import { TableModifiersComponent } from './component/table-modifiers/table-modifiers.component';
import { EmptyStringToNullDirective } from './directive/transformers/empty-string-to-null.directive';
import { StringArrayPipe } from './pipe/string-array.pipe';
import { IFrameResizerDirective } from './directive/iframe-resizer/iframe-resizer.directive';
import { PageHeaderComponent } from './component/page-header/page-header.component';
import { MomentDatePipe } from './pipe/moment-date.pipe';
import { FormatDurationFromSecondPipe } from './pipe/format-duration-from-second.pipe';
import { WarningCardComponent } from './component/warning-card/warning-card.component';
import { ScwMatUiModule } from './component/scw-mat-ui/scw-mat-ui.module';
import { PageConfigurationComponent } from './component/page-configuration/page-configuration.component';
import { DownloadFilteredDataModalComponent } from './component/download-filtered-data-modal/download-filtered-data-modal.component';
import { ToggleNestedTableDirective } from './directive/toggle-nested-table/toggle-nested-table.directive';
import { DownloadDataModalComponent } from './component/download-data-modal/download-data-modal.component';
import { QuillModule } from 'ngx-quill';
import { GetPipe } from './pipe/lodash/get.pipe';
import { IsEmptyPipe } from './pipe/lodash/isEmpty.pipe';
import { ToLocaleStringPipe } from './pipe/to-locale-string.pipe';
import { MomentPipe } from './pipe/moment/moment.pipe';
import { FormatPipe } from './pipe/moment/format.pipe';
import { IndexOfPipe } from './pipe/index-of.pipe';
import { FilterVisibilityButtonComponent } from './component/page-header/filter-visibility-button/filter-visibility-button.component';
import { ReplacePipe } from './pipe/replace.pipe';
import { ToStringPipe } from './pipe/to-string.pipe';
import { InformativeModalComponent } from './component/informative-modal/informative-modal.component';
import { FileGeneratorComponent } from './component/file-generator/file-generator.component';
import { ProgressBarComponent } from './component/progress-bar/progress-bar.component';
import { NullishCoalescePipe } from './pipe/nullish-coelesce.pipe';
import { ToBooleanPipe } from './pipe/to-boolean.pipe';
import { EqualPipe } from './pipe/equal.pipe';
import { MapPipe } from './pipe/map.pipe';
import { getApiUrl } from '../utilities';
import { AfterViewInitDirective } from './directive/after-view-init.directive';

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true,
};

@NgModule({
  entryComponents: [
    NumberInputComponent,
    SearchableDropdownComponent,
  ],
  imports: [
    CommonModule,
    NgbModule,
    HttpClientModule,
    PerfectScrollbarModule,
    ClickOutsideModule,
    FormsModule,
    ReactiveFormsModule,
    InfiniteScrollModule,
    Ng2SmartTableModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    TreeModule,
    UiSwitchModule.forRoot({
      defaultBgColor: '#efefef',
      defaultBoColor: '#a6a4a4',
    }),
    NgxEchartsModule.forRoot({
      echarts: () => import('echarts'),
    }),
    ArchwizardModule,
    NgxDaterangepickerMd.forRoot(),
    RouterModule,
    MatProgressBarModule,
    FilterCardModule,
    DragDropModule,
    MatTabsModule,
    SelectModule,
    AngularMultiSelectModule,
    ScwMatUiModule,
    QuillModule.forRoot(),
  ],
  exports: [
    NgbModule,
    ToggleFullScreenDirective,
    AccordionAnchorDirective,
    AccordionLinkDirective,
    AccordionDirective,
    IFrameResizerDirective,
    CardToggleDirective,
    HttpClientModule,
    PerfectScrollbarModule,
    CardComponent,
    MainNavBarComponent,
    ModalBasicComponent,
    ModalAnimationComponent,
    SpinnerComponent,
    ClickOutsideModule,
    DataFilterPipe,
    StringArrayPipe,
    ToLocaleStringPipe,
    MomentPipe,
    FormatPipe,
    IndexOfPipe,
    FormsModule,
    ReactiveFormsModule,
    InfiniteScrollModule,
    CustomMaxDirective,
    CustomMinDirective,
    CustomMinDateDirective,
    CustomMaxDateDirective,
    Ng2SmartTableModule,
    NumberInputComponent,
    SearchableDropdownComponent,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    TreeCheckboxComponent,
    DatePickerRendererComponent,
    DatePickerComponent,
    DatatableComponent,
    CustomMaxLengthValidatorDirective,
    NgxDaterangepickerMd,
    CommonModule,
    NgxEchartsModule,
    UiSwitchModule,
    ArchwizardModule,
    AngularMultiSelectModule,
    DateRangePickerComponent,
    FilterCardModule,
    SideConfigBarComponent,
    DisableControlDirective,
    NgxDaterangepickerMd,
    FilterCardModule,
    TableModifiersComponent,
    EmptyStringToNullDirective,
    MatProgressBarModule,
    PageHeaderComponent,
    PageConfigurationComponent,
    MomentDatePipe,
    WarningCardComponent,
    ScwMatUiModule,
    FormatDurationFromSecondPipe,
    DownloadFilteredDataModalComponent,
    ToggleNestedTableDirective,
    NullishCoalescePipe,
    GetPipe,
    IsEmptyPipe,
    ReplacePipe,
    ToStringPipe,
    ToBooleanPipe,
    EqualPipe,
    MapPipe,
    DownloadDataModalComponent,
    QuillModule,
    InformativeModalComponent,
    FileGeneratorComponent,
    ProgressBarComponent,
    AfterViewInitDirective,
  ],
  declarations: [
    ToggleFullScreenDirective,
    AccordionAnchorDirective,
    AccordionLinkDirective,
    AccordionDirective,
    CardToggleDirective,
    IFrameResizerDirective,
    CardComponent,
    ModalBasicComponent,
    ModalAnimationComponent,
    SpinnerComponent,
    DataFilterPipe,
    StringArrayPipe,
    ToLocaleStringPipe,
    MomentPipe,
    FormatPipe,
    IndexOfPipe,
    CustomMaxDirective,
    CustomMinDirective,
    CustomMinDateDirective,
    CustomMaxDateDirective,
    TreeCheckboxComponent,
    DatePickerComponent,
    DatePickerRendererComponent,
    DatatableComponent,
    CustomMaxLengthValidatorDirective,
    MainNavBarComponent,
    DateRangePickerComponent,
    SearchBoxComponent,
    SideConfigBarComponent,
    DisableControlDirective,
    PaginationCustomizerDirective,
    NumberInputComponent,
    SearchableDropdownComponent,
    TableModifiersComponent,
    EmptyStringToNullDirective,
    PageHeaderComponent,
    MomentDatePipe,
    WarningCardComponent,
    FormatDurationFromSecondPipe,
    PageConfigurationComponent,
    DownloadFilteredDataModalComponent,
    ToggleNestedTableDirective,
    NullishCoalescePipe,
    GetPipe,
    IsEmptyPipe,
    ReplacePipe,
    ToStringPipe,
    ToBooleanPipe,
    EqualPipe,
    MapPipe,
    DownloadDataModalComponent,
    FilterVisibilityButtonComponent,
    InformativeModalComponent,
    FileGeneratorComponent,
    ProgressBarComponent,
    AfterViewInitDirective,
  ],
  providers: [
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandlerService,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AppHttpInterceptor,
      multi: true,
    },
    {
      provide: 'API_BASE_URL',
      useFactory: getApiUrl,
    },
  ],
  schemas: [NO_ERRORS_SCHEMA],
})
export class SharedModule {}
